/**
 * The scan autopilot's own status, polled only while a batch is running.
 *
 * Its own hook rather than a few lines inside the leads hook, for the same
 * reason the brain's engine has one (`brain/useBrainAutopilot.ts`): the engine
 * strip and the lists it feeds fail separately and must keep failing
 * separately. A strip whose endpoint is down may not blank the call queue, and
 * a queue that failed to load must still let an operator switch the engine off. That is also why
 * the loader swallows its error instead of surfacing one.
 *
 * It also untangles two readers that used to share one piece of state. The
 * Skannid tab's strip needs the whole engine (switch, settings, run log); the
 * call queue only needs to know whether a batch is in flight, so it can pull
 * new rows in while one is. Both now call this hook with their own list to
 * refresh, which is what lets the strip live on a different tab from the queue
 * it feeds.
 *
 * WHILE A BATCH RUNS, THE LIST IS PULLED IN WITH IT. A batch takes seconds to
 * minutes and its whole visible effect is new rows in a table, so refreshing
 * the engine's numbers without refreshing the list would show a counter
 * climbing over a table that never changes. The polling stops the moment the
 * batch does: a table nobody is watching should not be asking for updates.
 */
import { useCallback, useEffect, useState } from "react";
import { getAutopilot, type AutopilotState } from "../../../lib/scanLeads";

/** How often to re-read the engine while it is working. */
const BUSY_POLL_MS = 5000;

/**
 * @param onBatchTick called on every poll while a batch runs: the caller's own
 *   list reload. Keep it stable (a `useCallback`), or the poll restarts on
 *   every render.
 */
export function useScanAutopilot(onBatchTick: () => void) {
  const [state, setState] = useState<AutopilotState | null>(null);

  const load = useCallback(async () => {
    try {
      setState(await getAutopilot());
    } catch {
      // Informational. The list that called this hook stands on its own and
      // must not go blank because this one endpoint failed.
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!state?.busy) return;
    const timer = setInterval(() => {
      void load();
      onBatchTick();
    }, BUSY_POLL_MS);
    return () => clearInterval(timer);
  }, [state?.busy, load, onBatchTick]);

  return { state, refresh: load };
}
